import { createAsyncThunk } from "@reduxjs/toolkit";
import { MARKETING } from "constants/api";
import { toMarketingPage } from "utils";
import axios from "utils/http";

export interface IFetchPartnersResponse extends ApiResponse {
  data: IMarketingPartnerList;
}

export interface IPartnerResponse extends ApiResponse {
  data: IMarketingPartnerDetails;
}

export interface IPartnerBranchesResponse extends ApiResponse {
  data: IPartnerBranchList;
}

export interface IPartnerBranchResponse extends ApiResponse {
  data: IPartnerBranch;
}

export interface IPartnerBranchEmailResponse extends ApiResponse {
  data: IPartnerBranchEmailResult;
}

export interface IPartnerPaymentsResponse extends ApiResponse {
  data: IPartnerPaymentList;
}

export interface IPartnerPaymentResponse extends ApiResponse {
  data: IPartnerPayment;
}

export interface IPartnerStatusResponse extends ApiResponse {
  data: IPartnerStatusResult;
}

export interface IPartnerActivityResponse extends ApiResponse {
  data: IPartnerActivityList;
}

export interface IRevokeDevicesResponse extends ApiResponse {
  data: IRevokeDevicesResult;
}

export interface ISavedPartner {
  partner: IMarketingPartnerDetails;
  branches: IPartnerBranchList | null;
  hasBranchError: boolean;
}

export interface IPartnerPage<T> {
  id: number;
  list: IMarketingPage<T>;
}

export interface IRecordPaymentResponse extends ApiResponse {
  data: IRecordPaymentResult;
}

export interface IPartnerDevicesResponse extends ApiResponse {
  data: IPartnerDeviceList;
}

export interface IFetchPartnersArgs extends ReduxAction {
  page: number;
  queries: Partial<IPartnerFilters>;
}

export interface IPartnerArgs extends ReduxAction {
  id: number;
}

export interface IPartnerSearchArgs extends ReduxAction {
  view: MarketingPartnerView;
  search: string;
}

export const PARTNER_PICKER_PAGE_SIZE = 20;

export interface IPartnerListArgs extends ReduxAction {
  id: number;
  page?: number;
  perPage?: number;
}

export interface ISavePartnerArgs extends ReduxAction {
  id?: number | null;
  payload: ISavePartnerPayload;
}

export interface IRecordPaymentArgs extends ReduxAction {
  id: number;
  payload: IRecordPaymentPayload;
  idempotencyKey: string;
}

export interface IPartnerBranchArgs extends ReduxAction {
  id: number;
  branchId?: number | null;
  payload: IPartnerBranchPayload;
}

export interface IPartnerBranchEmailArgs extends ReduxAction {
  id: number;
  branchId: number;
}

export interface IReversePaymentArgs extends ReduxAction {
  id: number;
  paymentId: number;
  payload: IReversePaymentPayload;
}

const MULTIPART_CONFIG = {
  headers: { "Content-Type": "multipart/form-data" },
};

const appendValue = (formData: FormData, key: string, value: unknown) => {
  if (value === undefined || value === null) return;

  if (typeof value === "boolean") {
    formData.append(key, value ? "1" : "0");
    return;
  }

  if (value instanceof Blob) {
    formData.append(key, value);
    return;
  }

  formData.append(key, String(value));
};

export const toPartnerFormData = (
  payload: ISavePartnerPayload,
  isUpdating: boolean
) => {
  const formData = new FormData();
  const { branches, ...fields } = payload;

  if (isUpdating) formData.append("_method", "PATCH");

  Object.entries(fields).forEach(([key, value]) =>
    appendValue(formData, key, value)
  );

  branches.forEach((branch, index) =>
    Object.entries(branch).forEach(([key, value]) =>
      appendValue(formData, `branches[${index}][${key}]`, value)
    )
  );

  return formData;
};

const toPartnerDetails = (
  partner: IMarketingPartnerDetails
): IMarketingPartnerDetails => ({
  ...partner,
  branches_count: Number(partner.branches_count) || 0,
  active_branches_count: Number(partner.active_branches_count) || 0,
  payments_count: Number(partner.payments_count) || 0,
  devices_count: Number(partner.devices_count) || 0,
});

const getPartnerBranches = async (id: number, page = 1, perPage?: number) => {
  const params: Record<string, number> = { page };
  if (perPage) params.per_page = perPage;

  const { data }: IPartnerBranchesResponse = await axios.get(
    MARKETING.PARTNER_BRANCHES.replace(":id", String(id)),
    { params }
  );

  return toMarketingPage<IPartnerBranch>(data);
};

export const PARTNER_BRANCHES_MAX = 100;

export const toPaymentFormData = (payload: IRecordPaymentPayload) => {
  const formData = new FormData();

  Object.entries(payload).forEach(([key, value]) =>
    appendValue(formData, key, value)
  );

  return formData;
};

export const PARTNERS_LOAD_ERROR = "Could not load partners. Please try again.";

const isPartnersList = (value: unknown): value is IMarketingPartnerList => {
  if (!value || typeof value !== "object") return false;
  const data = value as Record<string, unknown>;
  const isNumber = (item: unknown) => typeof item === "number" && Number.isFinite(item);
  const counts = data.tab_counts as Record<string, unknown> | undefined;
  return Array.isArray(data.data) &&
    ["current_page", "last_page", "total"].every((key) => isNumber(data[key])) &&
    (!counts || ["all", "onboarding", "live"].every((key) => isNumber(counts[key]))) &&
    data.data.every((item: unknown) => {
      if (!item || typeof item !== "object") return false;
      const row = item as Record<string, unknown>;
      return isNumber(row.id) && typeof row.name === "string" && typeof row.status === "string" &&
        ["branches", "prepaid_kobo", "unspent_kobo", "spent_kobo"].every((key) => isNumber(row[key]));
    });
};

export const fetchPartners = createAsyncThunk<
  IMarketingPartnerList,
  IFetchPartnersArgs,
  { rejectValue: string }
>(
  MARKETING.PARTNERS,
  async (
    { page, queries, onFailure }: IFetchPartnersArgs,
    { rejectWithValue }
  ) => {
    try {
      const params: Record<string, string | number> = { page };

      Object.entries(queries ?? {}).forEach(([key, value]) => {
        if (value) params[key] = value;
      });

      const response: IFetchPartnersResponse = await axios.get(
        MARKETING.PARTNERS,
        { params }
      );
      if (response.status === false || !isPartnersList(response.data)) {
        throw new Error(PARTNERS_LOAD_ERROR);
      }
      return response.data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue((error as { isForbidden?: boolean } | null)?.isForbidden
        ? "You do not have permission to view partners."
        : PARTNERS_LOAD_ERROR);
    }
  }
);

export const searchPartners = createAsyncThunk(
  `${MARKETING.PARTNERS}/search`,
  async (
    { view, search, onSuccess, onFailure }: IPartnerSearchArgs,
    { rejectWithValue }
  ) => {
    try {
      const params: Record<string, string | number> = {
        view,
        page: 1,
        per_page: PARTNER_PICKER_PAGE_SIZE,
      };

      if (search) params.search = search;

      const { data }: IFetchPartnersResponse = await axios.get(
        MARKETING.PARTNERS,
        { params }
      );
      const page = toMarketingPage<IMarketingPartnerRow>(data);
      const result: IPartnerSearchResult = {
        options: page.data
          .filter((row) => view !== "live" || row.status === "live")
          .map(({ id, name, status }) => ({ id, name, status })),
        total: page.total,
        hasMore: page.total > page.data.length,
      };

      if (onSuccess) onSuccess(result);
      return result;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchPartner = createAsyncThunk(
  MARKETING.PARTNER_DETAIL,
  async ({ id, onSuccess, onFailure }: IPartnerArgs, { rejectWithValue }) => {
    try {
      const { data }: IPartnerResponse = await axios.get(
        MARKETING.PARTNER_DETAIL.replace(":id", String(id))
      );
      const partner = toPartnerDetails(data);

      if (onSuccess) onSuccess(partner);
      return partner;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const savePartner = createAsyncThunk(
  `${MARKETING.PARTNERS}/save`,
  async (
    { id, payload, onSuccess, onFailure }: ISavePartnerArgs,
    { rejectWithValue }
  ) => {
    try {
      const endpoint = id
        ? MARKETING.PARTNER_DETAIL.replace(":id", String(id))
        : MARKETING.PARTNERS;

      const { data }: IPartnerResponse = await axios.post(
        endpoint,
        toPartnerFormData(payload, Boolean(id)),
        MULTIPART_CONFIG
      );

      const saved: ISavedPartner = {
        partner: toPartnerDetails(data),
        branches: null,
        hasBranchError: false,
      };

      if (payload.branches.length) {
        try {
          saved.branches = await getPartnerBranches(
            saved.partner.id,
            1,
            PARTNER_BRANCHES_MAX
          );
        } catch {
          saved.hasBranchError = true;
        }
      }

      if (onSuccess) onSuccess(saved);
      return saved;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const recordPartnerPayment = createAsyncThunk(
  MARKETING.PARTNER_PAYMENTS,
  async (
    { id, payload, idempotencyKey, onSuccess, onFailure }: IRecordPaymentArgs,
    { rejectWithValue }
  ) => {
    try {
      const { data }: IRecordPaymentResponse = await axios.post(
        MARKETING.PARTNER_PAYMENTS.replace(":id", String(id)),
        toPaymentFormData(payload),
        {
          headers: {
            ...MULTIPART_CONFIG.headers,
            "Idempotency-Key": idempotencyKey,
          },
        }
      );

      if (onSuccess) onSuccess(data);
      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchPartnerBranches = createAsyncThunk(
  MARKETING.PARTNER_BRANCHES,
  async (
    { id, page = 1, perPage, onSuccess, onFailure }: IPartnerListArgs,
    { rejectWithValue }
  ) => {
    try {
      const list = await getPartnerBranches(id, page, perPage);
      const result: IPartnerPage<IPartnerBranch> = { id, list };

      if (onSuccess) onSuccess(result);
      return result;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchPartnerPayments = createAsyncThunk(
  `${MARKETING.PARTNER_PAYMENTS}/list`,
  async (
    { id, page = 1, perPage, onFailure }: IPartnerListArgs,
    { rejectWithValue }
  ) => {
    try {
      const params: Record<string, number> = { page };
      if (perPage) params.per_page = perPage;

      const { data }: IPartnerPaymentsResponse = await axios.get(
        MARKETING.PARTNER_PAYMENTS.replace(":id", String(id)),
        { params }
      );
      const result: IPartnerPage<IPartnerPayment> = {
        id,
        list: toMarketingPage<IPartnerPayment>(data),
      };

      return result;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchPartnerDevices = createAsyncThunk(
  MARKETING.PARTNER_DEVICES,
  async ({ id, page = 1, perPage = 15, onFailure }: IPartnerListArgs, { rejectWithValue }) => {
    try {
      const { data }: IPartnerDevicesResponse = await axios.get(
        MARKETING.PARTNER_DEVICES.replace(":id", String(id)),
        { params: { page, per_page: perPage } }
      );

      return { id, list: toMarketingPage<IPartnerDevice>(data) };
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const revokePartnerDevice = createAsyncThunk(
  MARKETING.REVOKE_DEVICE,
  async ({ id, onSuccess, onFailure }: IPartnerArgs, { rejectWithValue }) => {
    try {
      const { message }: ApiResponse = await axios.post(
        MARKETING.REVOKE_DEVICE.replace(":id", String(id))
      );

      if (onSuccess) onSuccess(message);
      return id;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const suspendPartner = createAsyncThunk(
  MARKETING.PARTNER_SUSPEND,
  async ({ id, onSuccess, onFailure }: IPartnerArgs, { rejectWithValue }) => {
    try {
      const { data, message }: IPartnerStatusResponse = await axios.post(
        MARKETING.PARTNER_SUSPEND.replace(":id", String(id))
      );

      if (onSuccess) onSuccess(message);
      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const reinstatePartner = createAsyncThunk(
  MARKETING.PARTNER_REINSTATE,
  async ({ id, onSuccess, onFailure }: IPartnerArgs, { rejectWithValue }) => {
    try {
      const { data, message }: IPartnerStatusResponse = await axios.post(
        MARKETING.PARTNER_REINSTATE.replace(":id", String(id))
      );

      if (onSuccess) onSuccess(message);
      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const savePartnerBranch = createAsyncThunk(
  `${MARKETING.PARTNER_BRANCHES}/save`,
  async (
    { id, branchId, payload, onSuccess, onFailure }: IPartnerBranchArgs,
    { rejectWithValue }
  ) => {
    try {
      const { data, message }: IPartnerBranchResponse = branchId
        ? await axios.patch(
            MARKETING.PARTNER_BRANCH.replace(":id", String(id)).replace(
              ":branchId",
              String(branchId)
            ),
            payload
          )
        : await axios.post(
            MARKETING.PARTNER_BRANCHES.replace(":id", String(id)),
            payload
          );

      if (onSuccess) onSuccess(message);
      return { id, branch: data };
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const sendBranchSignInEmail = createAsyncThunk(
  MARKETING.PARTNER_BRANCH_SIGN_IN_EMAIL,
  async (
    { id, branchId, onSuccess, onFailure }: IPartnerBranchEmailArgs,
    { rejectWithValue }
  ) => {
    try {
      const { data, message }: IPartnerBranchEmailResponse = await axios.post(
        MARKETING.PARTNER_BRANCH_SIGN_IN_EMAIL.replace(":id", String(id)).replace(
          ":branchId",
          String(branchId)
        )
      );

      if (onSuccess) onSuccess(message);
      return { id, branchId, result: data };
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const reversePartnerPayment = createAsyncThunk(
  MARKETING.PARTNER_PAYMENT_REVERSE,
  async (
    { id, paymentId, payload, onSuccess, onFailure }: IReversePaymentArgs,
    { rejectWithValue }
  ) => {
    try {
      const { data, message }: IPartnerPaymentResponse = await axios.post(
        MARKETING.PARTNER_PAYMENT_REVERSE.replace(":id", String(id)).replace(
          ":paymentId",
          String(paymentId)
        ),
        payload
      );

      if (onSuccess) onSuccess(message);
      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const fetchPartnerActivity = createAsyncThunk(
  MARKETING.PARTNER_ACTIVITY,
  async (
    { id, page = 1, onFailure }: IPartnerListArgs,
    { rejectWithValue }
  ) => {
    try {
      const { data }: IPartnerActivityResponse = await axios.get(
        MARKETING.PARTNER_ACTIVITY.replace(":id", String(id)),
        { params: { page } }
      );
      const result: IPartnerPage<IPartnerActivity> = {
        id,
        list: toMarketingPage<IPartnerActivity>(data),
      };

      return result;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);

export const revokeAllPartnerDevices = createAsyncThunk(
  MARKETING.PARTNER_DEVICES_REVOKE_ALL,
  async ({ id, onSuccess, onFailure }: IPartnerArgs, { rejectWithValue }) => {
    try {
      const { data }: IRevokeDevicesResponse = await axios.post(
        MARKETING.PARTNER_DEVICES_REVOKE_ALL.replace(":id", String(id))
      );

      if (onSuccess) onSuccess(data);
      return data;
    } catch (error) {
      if (onFailure) onFailure(error as IErrorResponse);
      return rejectWithValue({ error });
    }
  }
);
